import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent } from "@/hooks/useCookieConsent";

const GA_ID = "G-DEJ5PBG252";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    clarity?: (...args: unknown[]) => void;
  }
}

/**
 * Drives Google Analytics 4 in line with the visitor's cookie-consent choice.
 * - Updates Consent Mode v2 whenever the analytics/functional preference changes.
 * - Sends a manual page_view on each SPA route change (only recorded once consent is granted).
 * Mounted inside Layout so it sits within CookieConsentProvider.
 */
export function GoogleAnalytics() {
  const { analytics, functional, hasConsented } = useCookieConsent();
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  // Sync consent state to gtag.
  // Google signals follow an explicit stored choice only: the US functional
  // default (item 100) is first-party and must not grant anything to Google.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const googleFunctional = hasConsented && functional;
    window.gtag("consent", "update", {
      analytics_storage: analytics ? "granted" : "denied",
      functionality_storage: googleFunctional ? "granted" : "denied",
      personalization_storage: googleFunctional ? "granted" : "denied",
    });
  }, [analytics, functional, hasConsented]);

  // Sync analytics consent to Microsoft Clarity.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.clarity !== "function") return;
    window.clarity("consent", !!analytics);
  }, [analytics, hasConsented]);

  // Track SPA page views.
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.gtag !== "function") return;
    const path = location.pathname + location.search;
    if (lastPath.current === path) return;
    lastPath.current = path;
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
      send_to: GA_ID,
    });
  }, [location.pathname, location.search]);

  return null;
}